import PropTypes from "prop-types";
import { UserAddOutlined, CloseOutlined, Avatar, Tooltip } from "@/config/antd";

// Generates a consistent background color from a string (name/id)
const getAvatarColor = (str = "") => {
  const colors = [
    "#4A90D9",
    "#7B68EE",
    "#20B2AA",
    "#FF6B6B",
    "#FFA500",
    "#32CD32",
    "#9370DB",
    "#FF69B4",
  ];
  const index =
    str.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) %
    colors.length;
  return colors[index];
};

const AddPeopleCard = ({ title, onClick, members = [], onRemoveMember }) => (
  <div
    onClick={onClick}
    className="w-full bg-[#F8F9FA] rounded-lg p-6 hover:bg-gray-100 transition-colors duration-200 cursor-pointer">

    {/* Icon + label row — always visible */}
    <div className="flex flex-col items-center justify-center mb-3">
      <div className="relative mb-2">
        <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
          <UserAddOutlined className="text-gray-400 text-xl" />
        </div>
        <div className="absolute -top-1 -right-1 w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
          <span className="text-gray-400 text-lg">+</span>
        </div>
      </div>
      <h3 className="text-[#666666] font-bold text-small">{title}</h3>
    </div>

    {/* Body: chips when members exist, placeholder text otherwise */}
    {members.length > 0 ? (
      <div className="flex flex-wrap justify-center gap-2">
        {members.map((member) => (
          <Tooltip key={member.id} title={member.name} placement="top">
            <div
              /* Stop the card's onClick from firing when interacting with chips */
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-2 bg-white border border-gray-200 rounded-full pl-1 pr-2 py-1 shadow-sm hover:shadow-md transition-shadow duration-200">

              {/* Avatar with colour-coded initials fallback */}
              <Avatar
                src={member.avatar || undefined}
                size={28}
                shape="circle"
                style={{
                  backgroundColor: !member.avatar
                    ? getAvatarColor(member.name || member.id)
                    : undefined,
                  flexShrink: 0,
                  fontSize: "11px",
                  fontWeight: 600,
                }}>
                {/* First two initials when no profile image is available */}
                {!member.avatar &&
                  (member.name || "U")
                    .split(" ")
                    .slice(0, 2)
                    .map((word) => word[0]?.toUpperCase())
                    .join("")}
              </Avatar>

              {/* Truncated name */}
              <span className="text-xs font-medium text-gray-700 max-w-[100px] truncate leading-none">
                {member.name}
              </span>

              {/* Remove button — isolated from the card click */}
              {onRemoveMember && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveMember(member);
                  }}
                  className="flex items-center justify-center w-4 h-4 rounded-full bg-gray-100 hover:bg-red-100 hover:text-red-500 text-gray-400 transition-colors duration-150 flex-shrink-0"
                  aria-label={`Remove ${member.name}`}>
                  <CloseOutlined style={{ fontSize: "8px" }} />
                </button>
              )}
            </div>
          </Tooltip>
        ))}
      </div>
    ) : (
      <p className="text-center text-[#666666] text-small">
        No members selected. Click to select new
      </p>
    )}
  </div>
);

AddPeopleCard.propTypes = {
  title: PropTypes.string,
  onClick: PropTypes.func,
  members: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      avatar: PropTypes.string,
    })
  ),
  onRemoveMember: PropTypes.func,
};

export default AddPeopleCard;
